<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import AppDialog from '@/components/ui/Dialog.vue'
import AdvancesCard from '@/components/ui/AdvancesCard.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import Tag from 'primevue/tag'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { storeSalesApi, type StoreSale } from '@/services/storeSales'
import type { QuoteAdvance } from '@/services/quotes'
import { formatCurrency, formatShortDate } from '@/utils/format'

/**
 * Ficha de una venta de tienda.
 *
 * No hay artículos, notas de seguimiento ni estados logísticos: la venta ya
 * ocurrió. La ficha enseña la cabecera con la copia del cliente, el valor, la
 * facturación y los anticipos.
 */

const route = useRoute()
const router = useRouter()
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('store-sales.index')

const sale = ref<StoreSale | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)
const actionError = ref<string | null>(null)
const acting = ref(false)

const saleId = computed(() => Number.parseInt(String(route.params.id ?? ''), 10))

const advances = ref<QuoteAdvance[]>([])
const advancesTotal = ref('0')
const balance = ref('0')

function valor(texto: string | null, ausente = '—'): string {
  return texto === null || texto === '' ? ausente : texto
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = null

  try {
    const { data } = await storeSalesApi.get(saleId.value)
    sale.value = data.data
    advances.value = data.data.advances
    advancesTotal.value = data.data.advances_total
    balance.value = data.data.balance
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    loadError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar la venta.'
  } finally {
    loading.value = false
  }
}

function cambiaAnticipos(totals: { advances_total: string; balance: string; advances: QuoteAdvance[] }): void {
  advancesTotal.value = totals.advances_total
  balance.value = totals.balance
  advances.value = totals.advances
}

const invoiceVisible = ref(false)
const invoiceNumber = ref('')
const invoiceError = ref<string | null>(null)
const savingInvoice = ref(false)

function askInvoice(): void {
  invoiceNumber.value = sale.value?.invoice_number ?? ''
  invoiceError.value = null
  invoiceVisible.value = true
}

async function confirmInvoice(): Promise<void> {
  if (sale.value === null) return

  if (invoiceNumber.value.trim() === '') {
    invoiceError.value = 'Escribe el número de factura para marcarla como facturada.'
    return
  }

  savingInvoice.value = true
  invoiceError.value = null

  try {
    const { data } = await storeSalesApi.markInvoiced(sale.value.id, invoiceNumber.value.trim())
    sale.value = data.data
    invoiceVisible.value = false
  } catch (cause: unknown) {
    const maybeAxios = cause as {
      response?: { data?: { error?: { message?: string; details?: { fields?: Record<string, string[]> } } } }
    }
    invoiceError.value = maybeAxios.response?.data?.error?.details?.fields?.invoice_number?.[0]
      ?? maybeAxios.response?.data?.error?.message
      ?? 'No se pudo marcar la venta como facturada.'
  } finally {
    savingInvoice.value = false
  }
}

const deleteVisible = ref(false)
const deleting = ref(false)

async function confirmDelete(): Promise<void> {
  if (sale.value === null) return

  deleting.value = true
  actionError.value = null

  try {
    await storeSalesApi.remove(sale.value.id)
    deleteVisible.value = false
    await router.push(destinoListado.value)
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    actionError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar la venta.'
  } finally {
    deleting.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="space-y-4">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0">
        <h1 class="font-mono text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ sale?.sale_number ?? 'Venta de tienda' }}
        </h1>
        <p
          v-if="sale !== null"
          class="mt-1 text-sm text-surface-500 dark:text-surface-400"
        >
          {{ formatShortDate(sale.sale_date) }} · {{ sale.customer_name }}
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          @click="volverAlListado()"
        />
        <template v-if="sale !== null">
          <Button
            label="Editar"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            @click="router.push({ name: 'store-sales.edit', params: { id: String(sale.id) } })"
          />
          <Button
            v-if="!sale.is_invoiced"
            label="Marcar como facturada"
            icon="pi pi-check-circle"
            outlined
            @click="askInvoice"
          />
          <Button
            label="Borrar"
            icon="pi pi-trash"
            severity="danger"
            outlined
            @click="deleteVisible = true"
          />
        </template>
      </div>
    </header>

    <Message
      v-if="actionError !== null"
      severity="error"
      role="alert"
      :closable="false"
    >
      {{ actionError }}
    </Message>

    <Message
      v-if="loadError !== null"
      severity="error"
      role="alert"
      :closable="false"
    >
      {{ loadError }}
    </Message>

    <AppCard v-if="loading">
      <div class="flex flex-col gap-3">
        <Skeleton
          v-for="linea in 6"
          :key="linea"
          height="1rem"
          class="w-full"
        />
      </div>
    </AppCard>

    <template v-else-if="sale !== null">
      <AppCard>
        <template #header>
          <div class="flex flex-wrap items-center justify-between gap-3">
            <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
              Datos de la venta
            </h2>
            <Tag
              :value="sale.is_invoiced ? 'FACTURADA' : 'PENDIENTE'"
              :severity="sale.is_invoiced ? 'success' : 'secondary'"
            />
          </div>
        </template>

        <dl class="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Cliente
            </dt>
            <dd class="mt-1 font-medium text-surface-900 dark:text-surface-100">
              {{ sale.customer_name }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Documento
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.document) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Correo
            </dt>
            <dd class="mt-1 break-words text-surface-900 dark:text-surface-100">
              {{ valor(sale.customer_email) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Teléfono
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.customer_phone) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Ciudad
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.customer_city) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Origen
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.customer_source) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Asesor
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.user_name) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Línea de atención
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.attention_line) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Tipo de facturación
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ sale.price_type === 'retail' ? 'Detal' : 'Mayoreo' }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Valor de la venta
            </dt>
            <dd
              class="mt-1 text-lg font-semibold tabular-nums text-surface-900 dark:text-surface-100"
              data-testid="store-sale-total"
            >
              {{ formatCurrency(sale.amount) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Factura
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ valor(sale.invoice_number) }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-surface-500 dark:text-surface-400">
              Fecha de venta
            </dt>
            <dd class="mt-1 text-surface-900 dark:text-surface-100">
              {{ formatShortDate(sale.sale_date) }}
            </dd>
          </div>
        </dl>
      </AppCard>

      <AdvancesCard
        :quote-id="sale.id"
        kind="store"
        :advances="advances"
        :advances-total="advancesTotal"
        :balance="balance"
        @cambia="cambiaAnticipos"
      />
    </template>

    <AppDialog
      v-model:visible="invoiceVisible"
      title="Marcar como facturada"
      confirm-label="Facturar"
      fullscreen-mobile
    >
      <div class="space-y-4">
        <p class="text-sm text-surface-600 dark:text-surface-300">
          La venta <strong>{{ sale?.sale_number }}</strong> quedará como facturada.
        </p>

        <div>
          <label
            for="store-sale-detail-invoice"
            class="mb-1 block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Número de factura
          </label>
          <InputText
            id="store-sale-detail-invoice"
            v-model="invoiceNumber"
            class="w-full"
          />
        </div>

        <Message
          v-if="invoiceError"
          severity="error"
          :closable="false"
          variant="simple"
          role="alert"
        >
          {{ invoiceError }}
        </Message>
      </div>

      <template #footer>
        <Button
          label="Cancelar"
          severity="secondary"
          @click="invoiceVisible = false"
        />
        <Button
          label="Facturar"
          :loading="savingInvoice"
          @click="confirmInvoice"
        />
      </template>
    </AppDialog>

    <AppDialog
      v-model:visible="deleteVisible"
      :title="`Borrar venta de ${sale?.customer_name ?? ''}`"
      confirm-label="Borrar"
      fullscreen-mobile
    >
      <p class="text-sm text-surface-600 dark:text-surface-300">
        Se borrará la venta <strong>{{ sale?.sale_number }}</strong> y sus anticipos. Esta
        acción no se puede deshacer.
      </p>

      <template #footer>
        <Button
          label="Cancelar"
          severity="secondary"
          @click="deleteVisible = false"
        />
        <Button
          label="Borrar"
          severity="danger"
          :loading="deleting"
          data-testid="store-sale-confirm-delete"
          @click="confirmDelete"
        />
      </template>
    </AppDialog>
  </div>
</template>
